import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useSiteInfo } from "./StoreInfoCard";
import { useStoreHours, formatStoreHours } from "../lib/hours";
import { StoreStatusBanner } from "./StoreStatusBanner";
import "./layout.css";

const ICON_PROPS = {
  viewBox: "0 0 24 24",
  width: 20,
  height: 20,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function IconBook() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <path d="M3 19a9 9 0 0 1 9 0 9 9 0 0 1 9 0" />
      <path d="M3 6a9 9 0 0 1 9 0 9 9 0 0 1 9 0" />
      <line x1="3" y1="6" x2="3" y2="19" />
      <line x1="12" y1="6" x2="12" y2="19" />
      <line x1="21" y1="6" x2="21" y2="19" />
    </svg>
  );
}

function IconGift() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 1 1 0-5c2.5 0 4.5 5 4.5 5s2-5 4.5-5a2.5 2.5 0 1 1 0 5" />
    </svg>
  );
}

function IconLogin() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <path d="M14 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2" />
      <path d="M9 12h12" />
      <path d="M18 15l3-3-3-3" />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <path d="M9 8V6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-7a2 2 0 0 1-2-2v-2" />
      <path d="M3 12h12" />
      <path d="M6 15l-3-3 3-3" />
    </svg>
  );
}

function IconAdmin() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <path d="M12 3l8 4v5c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
    </svg>
  );
}

export function Layout() {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const { data: siteInfo } = useSiteInfo();
  const { data: storeHours } = useStoreHours();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  async function onLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  return (
    <>
      <a href="#main" className="skip-link">
        {t("skipLink")}
      </a>

      <header className="site-header" role="banner">
        <div className="container site-header__inner">
          <Link to="/" className="brand" aria-label={t("brand")}>
            <span className="brand__mark" aria-hidden="true">P→</span>
            <span className="brand__name">passa a página</span>
          </Link>
          <nav aria-label={t("nav.browse")} className="site-nav">
            {!isHome && (
              <>
                <NavLink to="/browse" aria-label={t("nav.browse")} className="nav-item">
                  <span className="nav-item__icon"><IconBook /></span>
                  <span className="nav-item__label">{t("nav.browse")}</span>
                </NavLink>
                <NavLink to="/donate" aria-label={t("nav.donate")} className="nav-item">
                  <span className="nav-item__icon"><IconGift /></span>
                  <span className="nav-item__label">{t("nav.donate")}</span>
                </NavLink>
              </>
            )}
            {/* Horários is shown on every page (incl. home), just before the
                language switcher, so visitors can always find opening hours. */}
            <NavLink to="/marcacoes" aria-label={t("nav.hours")} className="nav-item">
              <span className="nav-item__icon"><IconCalendar /></span>
              <span className="nav-item__label">{t("nav.hours")}</span>
            </NavLink>
            <LanguageSwitcher />
            {loading ? null : user ? (
              <>
                <NavLink
                  to="/dashboard"
                  className="nav-item nav-item--points"
                  aria-label={t("dashboard.bookPoints", { count: user.points })}
                  title={t("dashboard.bookPoints", { count: user.points })}
                >
                  <span className="nav-item__icon"><IconBook /></span>
                  <span className="nav-item__count">{user.points}</span>
                </NavLink>
                {user.role === "admin" && (
                  <NavLink
                    to="/admin"
                    aria-label={t("nav.admin")}
                    className="nav-item"
                  >
                    <span className="nav-item__icon"><IconAdmin /></span>
                    <span className="nav-item__label">{t("nav.admin")}</span>
                  </NavLink>
                )}
                <button
                  type="button"
                  onClick={onLogout}
                  className="nav-item nav-item--button"
                  aria-label={t("nav.logout")}
                >
                  <span className="nav-item__icon"><IconLogout /></span>
                  <span className="nav-item__label">{t("nav.logout")}</span>
                </button>
              </>
            ) : (
              <NavLink to="/login" aria-label={t("nav.login")} className="nav-item">
                <span className="nav-item__icon"><IconLogin /></span>
                <span className="nav-item__label">{t("nav.login")}</span>
              </NavLink>
            )}
          </nav>
        </div>
      </header>

      <StoreStatusBanner />

      <main id="main" tabIndex={-1} className="site-main">
        <Outlet />
      </main>

      <footer className="site-footer" role="contentinfo">
        <div className="container site-footer__inner">
          <div className="site-footer__col site-footer__col--left">
            <p className="site-footer__addr">{t("footer.address")}</p>
            {storeHours && (
              <p className="site-footer__hours">
                {t("footer.open")}: <strong>{formatStoreHours(storeHours.days, t)}</strong>
              </p>
            )}
            <p className="site-footer__contact">
              <a href="mailto:geral@passa-a-pagina.eu">geral@passa-a-pagina.eu</a>
              {siteInfo?.phone && (
                <>
                  {" · "}
                  <a href={`tel:${siteInfo.phone.replace(/\s/g, "")}`}>{siteInfo.phone}</a>
                </>
              )}
            </p>
          </div>
          <div className="site-footer__col site-footer__col--center">
            <nav aria-label="Footer" className="site-footer__nav">
              <Link to="/contacto">{t("footer.contact")}</Link>
              <Link to="/privacidade">{t("footer.privacy")}</Link>
              <Link to="/termos">{t("footer.terms")}</Link>
              <Link to="/eliminar-dados">{t("footer.deleteData")}</Link>
            </nav>
            <p className="site-footer__copyright">
              {t("footer.copyright", { year: new Date().getFullYear() })}
            </p>
          </div>
          <div className="site-footer__col site-footer__col--right">
            {user && (
              <p className="site-footer__session" aria-label="Session info">
                <span className="sr-only">{t("footer.sessionLabel")}: </span>
                {user.email} · {user.role} · {t("dashboard.bookPoints", { count: user.points })}
              </p>
            )}
            <p className="site-footer__built-by">
              <span>{t("footer.builtBy")}</span>
              <img
                src="/kebrabsoluta-onda.png"
                alt="Kebrabsoluta Uni. Lda."
                width="80"
                height="20"
                loading="lazy"
              />
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
